/**
 * API hata sınıflandırması.
 *
 * En kritik kural: **4xx yanıtları asla otomatik tekrar edilmez.** Doğrulama
 * veya yetki hatası tekrar denenirse kuyruk sonsuza kadar döner ve operatör
 * sorunu hiç görmez. Yalnızca ağ hatası, 429 ve 5xx tekrar denenebilir.
 */
import type { ApiErrorCode, CorrelationId } from '@restaurant/contracts'

/** Ağ katmanına hiç ulaşamamış istekler bu durum kodunu taşır. */
export const NETWORK_ERROR_STATUS = 0

export interface ApiErrorDetails {
  readonly status: number
  readonly code: ApiErrorCode
  readonly message: string
  readonly errors?: Readonly<Record<string, readonly string[]>>
  readonly correlationId?: CorrelationId
  readonly retryAfterSeconds?: number
}

export class ApiError extends Error {
  readonly status: number
  readonly code: ApiErrorCode
  readonly errors: Readonly<Record<string, readonly string[]>> | undefined
  readonly correlationId: CorrelationId | undefined
  readonly retryAfterSeconds: number | undefined

  constructor(details: ApiErrorDetails) {
    super(details.message)
    this.name = 'ApiError'
    this.status = details.status
    this.code = details.code
    this.errors = details.errors
    this.correlationId = details.correlationId
    this.retryAfterSeconds = details.retryAfterSeconds
  }

  /** Sunucuya hiç ulaşılamadı — istek gönderilmiş olabilir de olmayabilir de. */
  get isNetworkError(): boolean {
    return this.status === NETWORK_ERROR_STATUS
  }

  /** Kimlik doğrulama gerekiyor; token yenilenip yeniden denenebilir. */
  get isUnauthenticated(): boolean {
    return this.status === 401
  }

  /** Yetki yok. Tekrar denemek durumu değiştirmez — kullanıcıya gösterilir. */
  get isForbidden(): boolean {
    return this.status === 403
  }

  /** Çakışma: eşzamanlı güncelleme veya geçersiz durum geçişi. */
  get isConflict(): boolean {
    return this.status === 409
  }

  /** Doğrulama hatası — alan bazlı mesajlar `errors` içinde. */
  get isValidationError(): boolean {
    return this.status === 422
  }

  get isRateLimited(): boolean {
    return this.status === 429
  }

  get isServerError(): boolean {
    return this.status >= 500
  }

  /**
   * Otomatik tekrar denemeye uygun mu?
   *
   * 4xx (429 hariç) terminaldir: tekrar denemek aynı sonucu üretir ve
   * kullanıcıya gösterilmesi gereken hatayı gizler.
   */
  get isRetryable(): boolean {
    return this.isNetworkError || this.isRateLimited || this.isServerError
  }
}

/** HTTP durum kodunu, sunucu kod göndermediğinde kullanılacak koda eşler. */
export function defaultErrorCode(status: number): ApiErrorCode {
  switch (status) {
    case NETWORK_ERROR_STATUS:
      return 'network_error'
    case 401:
      return 'unauthenticated'
    case 403:
      return 'forbidden'
    case 404:
      return 'not_found'
    case 409:
      return 'conflict'
    case 422:
      return 'validation_failed'
    case 429:
      return 'rate_limited'
    case 503:
      return 'service_unavailable'
    default:
      return status >= 500 ? 'server_error' : 'validation_failed'
  }
}

/**
 * `Retry-After` başlığını saniyeye çevirir.
 * Hem saniye biçimini hem HTTP-date biçimini destekler (RFC 9110).
 */
export function parseRetryAfter(
  headerValue: string | null,
  now: number = Date.now(),
): number | undefined {
  if (headerValue === null || headerValue.trim() === '') {
    return undefined
  }

  const seconds = Number(headerValue)
  if (Number.isFinite(seconds) && seconds >= 0) {
    return seconds
  }

  const timestamp = Date.parse(headerValue)
  if (Number.isNaN(timestamp)) {
    return undefined
  }

  return Math.max(0, Math.ceil((timestamp - now) / 1000))
}
